import {
  personFromName,
  projectStyle,
  type ColumnId,
  type Person,
  type Ticket,
  type TicketProject,
} from '@/components/tickets/data'
import { api } from '@/services/api'

interface ApiUser {
  id: string
  nom: string
  role: string
}

interface ApiProject {
  id: number
  nom: string
  project_manager_id: string
  project_manager_ids?: string[]
  project_manager_nom: string | null
  developer_ids?: string[]
  visibility?: string
}

interface ApiTicket {
  id: number
  titre: string
  description: string | null
  project_id: number
  chef_projet_id: string | null
  chef_projet_ids?: string[]
  assignee_ids: string[]
  echeance: string
  status: ColumnId
  ordre: number
  done_at: string | null
  reviewed_at: string | null
  time_spent_days: number | null
  time_spent_unit?: 'days' | 'hours' | null
  cp_time_spent_days?: number | null
  cp_time_spent_unit?: 'days' | 'hours' | null
}

interface ListResponse<T> {
  data: T[]
}

export interface TicketBoard {
  tickets: Ticket[]
  projects: TicketProject[]
  chefs: Person[]
  devs: Person[]
}

export interface TicketPayload {
  title: string
  description: string
  projectId: string
  projectName?: string
  cpIds: string[]
  assigneeIds: string[]
  dueDate: string
  status: ColumnId
  timeSpentDays?: number
  timeSpentUnit?: 'days' | 'hours'
  cpTimeSpentDays?: number | null
  cpTimeSpentUnit?: 'days' | 'hours' | null
  cpTimeTouched?: boolean
}

function toTicket(ticket: ApiTicket): Ticket {
  return {
    id: String(ticket.id),
    projectId: String(ticket.project_id),
    title: ticket.titre,
    description: ticket.description ?? '',
    assigneeIds: ticket.assignee_ids,
    cpIds: ticket.chef_projet_ids?.length
      ? ticket.chef_projet_ids
      : ticket.chef_projet_id
        ? [ticket.chef_projet_id]
        : [],
    dueDate: ticket.echeance.slice(0, 10),
    status: ticket.status,
    order: ticket.ordre,
    doneAt: ticket.done_at ? ticket.done_at.slice(0, 10) : null,
    reviewedAt: ticket.reviewed_at ? ticket.reviewed_at.slice(0, 10) : null,
    timeSpentDays: ticket.time_spent_days,
    timeSpentUnit: ticket.time_spent_unit === 'hours' ? 'hours' : ticket.time_spent_unit === 'days' ? 'days' : null,
    cpTimeSpentDays: ticket.cp_time_spent_days ?? null,
    cpTimeSpentUnit: ticket.cp_time_spent_unit === 'hours' ? 'hours' : ticket.cp_time_spent_unit === 'days' ? 'days' : null,
  }
}

function toApiBody(payload: TicketPayload) {
  const projectName = payload.projectName?.trim()
  return {
    titre: payload.title,
    description: payload.description,
    ...(projectName ? { project_nom: projectName } : { project_id: Number(payload.projectId) }),
    chef_projet_ids: payload.cpIds,
    assignee_ids: payload.assigneeIds,
    echeance: payload.dueDate,
    status: payload.status,
    ...(payload.timeSpentDays != null
      ? {
          time_spent_days: payload.timeSpentDays,
          time_spent_unit: payload.timeSpentUnit === 'hours' ? 'hours' : 'days',
        }
      : {}),
    ...(payload.cpTimeTouched
      ? {
          cp_time_spent_days: payload.cpTimeSpentDays ?? null,
          cp_time_spent_unit:
            payload.cpTimeSpentDays != null ? (payload.cpTimeSpentUnit === 'hours' ? 'hours' : 'days') : null,
        }
      : {}),
  }
}

export async function fetchTicketBoard(): Promise<TicketBoard> {
  const [ticketsResponse, projectsResponse, usersResponse, managersResponse] = await Promise.all([
    api.get<ListResponse<ApiTicket>>('/tickets'),
    api.get<ListResponse<ApiProject>>('/projects'),
    api.get<ListResponse<ApiUser>>('/users', { internal: 'true' }),
    api.get<ListResponse<ApiUser>>('/users/project-managers'),
  ])

  const projects = unwrapList<ApiProject>(projectsResponse).map((project): TicketProject => {
    const style = projectStyle(String(project.id))
    return {
      id: String(project.id),
      name: project.nom,
      badgeClass: style.badgeClass,
      dotClass: style.dotClass,
      managerIds: project.project_manager_ids?.length
        ? project.project_manager_ids
        : project.project_manager_id
          ? [project.project_manager_id]
          : [],
      developerIds: project.developer_ids ?? [],
      visibility: project.visibility === 'interne' ? 'interne' : 'externe',
    }
  }).sort((a, b) => a.name.localeCompare(b.name, 'fr'))

  const devs = unwrapList<ApiUser>(usersResponse)
    .filter((user) => user.role === 'admin' || user.role === 'super_admin')
    .map((user) => personFromName(user.id, user.nom))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'))

  const chefs = unwrapList<ApiUser>(managersResponse)
    .map((user) => personFromName(user.id, user.nom))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'))

  return {
    tickets: unwrapList<ApiTicket>(ticketsResponse).map(toTicket),
    projects,
    chefs,
    devs,
  }
}

export async function createTicket(payload: TicketPayload): Promise<Ticket> {
  const response = await api.post<ApiTicket>('/tickets', toApiBody(payload))
  return toTicket(response.data)
}

export async function updateTicket(id: string, payload: TicketPayload): Promise<Ticket> {
  const response = await api.patch<ApiTicket>(`/tickets/${id}`, toApiBody(payload))
  return toTicket(response.data)
}

export async function deleteTicket(id: string): Promise<void> {
  await api.delete<null>(`/tickets/${id}`)
}

export async function reorderTickets(
  items: Array<{
    id: string
    status: ColumnId
    order: number
    timeSpentDays?: number
    timeSpentUnit?: 'days' | 'hours'
    cpTimeSpentDays?: number | null
    cpTimeSpentUnit?: 'days' | 'hours' | null
    cpTimeTouched?: boolean
  }>
): Promise<Ticket[]> {
  if (items.length === 0) return []
  const response = await api.patch<ListResponse<ApiTicket>>('/tickets/order', {
    items: items.map((item) => ({
      id: Number(item.id),
      status: item.status,
      ordre: item.order,
      ...(item.timeSpentDays != null
        ? {
            time_spent_days: item.timeSpentDays,
            time_spent_unit: item.timeSpentUnit === 'hours' ? 'hours' : 'days',
          }
        : {}),
      ...(item.cpTimeTouched
        ? {
            cp_time_spent_days: item.cpTimeSpentDays ?? null,
            cp_time_spent_unit:
              item.cpTimeSpentDays != null ? (item.cpTimeSpentUnit === 'hours' ? 'hours' : 'days') : null,
          }
        : {}),
    })),
  })
  return unwrapList<ApiTicket>(response).map(toTicket)
}

function unwrapList<T>(response: { data: unknown }): T[] {
  const body = response.data
  if (Array.isArray(body)) return body as T[]
  if (body && typeof body === 'object' && Array.isArray((body as { data?: unknown }).data)) {
    return (body as { data: T[] }).data
  }
  return []
}
